import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, BookOpen, Globe, Loader2, CheckCircle2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { createGhlContact } from '@/integrations/ghl/client';

const Footer = () => {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [hp, setHp] = useState(''); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (hp) return; // bot caught
    if (!email.trim()) return;

    setSubmitting(true);
    const result = await createGhlContact({
      email: email.trim(),
      source: 'chuckboyce.com — footer newsletter',
      tags: ['newsletter', 'footer-signup'],
    });
    setSubmitting(false);

    if (result.success) {
      setDone(true);
      setEmail('');
      toast({ title: "You're in.", description: 'Thanks for subscribing — check your inbox soon.' });
    } else {
      toast({
        title: 'Something went wrong',
        description: 'error' in result ? result.error : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <footer className="border-t border-border bg-card/50">
      <div className="container mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <Link to="/" className="text-xl font-bold tracking-tight">
              <span className="text-foreground">Chuck</span>
              <span className="text-primary"> Boyce</span>
            </Link>
            <p className="mt-4 text-muted-foreground text-sm max-w-md">
              30 years of technical innovation and strategic leadership. Applying systems thinking
              to transform organizations and communities.
            </p>

            {/* Newsletter signup */}
            <form onSubmit={handleSubmit} className="mt-6 max-w-md">
              <label htmlFor="footer-email" className="block text-sm font-medium text-foreground mb-2">
                Stay in the loop
              </label>
              {done ? (
                <div className="flex items-center gap-2 text-sm text-primary">
                  <CheckCircle2 size={18} />
                  <span>Subscribed. Talk soon.</span>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    id="footer-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="flex-1 px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                  {/* Honeypot — hidden from real users */}
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={hp}
                    onChange={(e) => setHp(e.target.value)}
                    className="hidden"
                    aria-hidden="true"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60 inline-flex items-center gap-2"
                  >
                    {submitting ? <Loader2 size={16} className="animate-spin" /> : 'Subscribe'}
                  </button>
                </div>
              )}
              <p className="mt-2 text-xs text-muted-foreground">
                Occasional notes on systems thinking, leadership, and what I'm building. No spam.
              </p>
            </form>
          </div>

          <div>
            <h3 className="font-semibold text-foreground mb-4">Expertise</h3>
            <ul className="space-y-2">
              <li><Link to="/technical-operations" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Technical Operations</Link></li>
              <li><Link to="/digital-marketing" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Digital Marketing</Link></li>
              <li><Link to="/public-service" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Public Service</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-foreground mb-4">Connect</h3>
            <div className="flex gap-4">
              <a
                href="https://www.linkedin.com/in/chuckboyce"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-lg bg-muted hover:bg-primary/20 transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin size={20} className="text-muted-foreground hover:text-primary" />
              </a>
              <a
                href="https://www.amazon.com/author/chuckboyce"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-lg bg-muted hover:bg-primary/20 transition-colors"
                aria-label="Amazon Author Page"
              >
                <BookOpen size={20} className="text-muted-foreground hover:text-primary" />
              </a>
              <a
                href="https://beaconandsage.com"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-lg bg-muted hover:bg-primary/20 transition-colors"
                aria-label="Beacon & Sage"
              >
                <Globe size={20} className="text-muted-foreground hover:text-primary" />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-border flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Chuck Boyce. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
              Privacy Policy
            </Link>
            <span className="text-muted-foreground">•</span>
            <p className="text-xs text-muted-foreground">
              Technologist • Strategic Leader • Author
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
